import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { WarrantyDashboard } from "@/components/WarrantyDashboard";
import { PORTAL_NAVIGATION_ITEMS } from "@/lib/portal-navigation";
import { format } from "date-fns";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  Brain,
  CalendarIcon,
  Check,
  CircleDot,
  ClipboardCheck,
  Download,
  FileText,
  Flame,
  Gauge,
  Layers,
  MessageSquarePlus,
  Search,
  Share2,
  Settings2,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  Trophy,
  UserRound,
  X,
} from "lucide-react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ClaimsVolumeTrend } from "@/components/ClaimsVolumeTrend";
import { HistoricalFailureModes } from "@/components/HistoricalFailureModes";
import { ChartDataTable } from "@/components/ChartDataTable";
import { LiveDashboard } from "@/components/LiveDashboard";
import { usePortalRuntime } from "@/lib/portal-runtime";
import {
  activeCases as initialCases,
  auditTrail,
  claimsPerThousand,
  claimsControlChart,
  warrantyCostForecast,
  warrantyCostByLine,
  eightDStages,
  kpis,
  milestoneDueOffsets,
  pfmeaControl,
  predictiveAlerts,
  readAcross,
  teamMembers,
  type ReadAcrossStatus,
  loadPortalFixtureContext,
} from "@/lib/portal-fixtures";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Broadview Warranty Intelligence — Executive Dashboard" },
      {
        name: "description",
        content:
          "Governed warranty analysis with KPI evidence and closed-loop Problem Record navigation.",
      },
    ],
  }),
  component: PortalDashboard,
});

const severityTone: Record<string, string> = {
  Critical: "bg-destructive/15 text-destructive border-destructive/30",
  High: "bg-warning/15 text-warning border-warning/30",
  Medium: "bg-primary/10 text-primary border-primary/30",
  Low: "bg-muted text-muted-foreground border-border",
};

const statusTone: Record<string, string> = {
  "On Track": "bg-success/15 text-success",
  "At Risk": "bg-warning/15 text-warning",
  Overdue: "bg-destructive/15 text-destructive",
};

const D_CODES = ["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8"] as const;
const D_LABELS: Record<string, string> = {
  D1: "Team",
  D2: "Problem",
  D3: "Containment",
  D4: "Root Cause",
  D5: "Corrective",
  D6: "Implement",
  D7: "Prevent",
  D8: "Closure",
};

type CaseRow = (typeof initialCases)[number];
type LogEntry = {
  id: string;
  ts: string;
  author: string;
  text: string;
  kind: "note" | "stage" | "milestone" | "assign" | "due";
};
type Milestone = { done: boolean; assignee: string; due: string }; // due = ISO yyyy-mm-dd
type CaseState = {
  milestones: Record<string, Milestone>;
  log: LogEntry[];
};

function addDays(base: Date, days: number) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}
function isOverdue(m: Milestone) {
  if (m.done) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(m.due) < today;
}
function countOverdue(s: CaseState) {
  return D_CODES.reduce((n, c) => n + (isOverdue(s.milestones[c]) ? 1 : 0), 0);
}

function buildInitialState(rows: CaseRow[]): Record<string, CaseState> {
  const map: Record<string, CaseState> = {};
  const today = new Date();
  for (const r of rows) {
    const current = r.stage.split(" ")[0];
    const idx = D_CODES.indexOf(current as (typeof D_CODES)[number]);
    const offsets = milestoneDueOffsets[r.id] ?? [-6, -4, -2, 1, 4, 8, 12, 18];
    const milestones: Record<string, Milestone> = {};
    D_CODES.forEach((c, i) => {
      milestones[c] = {
        done: i < idx,
        assignee: r.owner,
        due: isoDay(addDays(today, offsets[i] ?? i * 4)),
      };
    });
    map[r.id] = {
      milestones,
      log: [
        {
          id: "seed-" + r.id,
          ts: "Today · 09:14",
          author: r.owner,
          text: `Case opened — ${r.issue}`,
          kind: "note",
        },
      ],
    };
  }
  return map;
}

function PortalDashboard() {
  const { mode } = usePortalRuntime();
  return mode === "live" ? <WarrantyDashboard /> : <DashboardPrototype />;
}

function DashboardPrototype({ liveMode = false }: Readonly<{ liveMode?: boolean }>) {
  const [cases, setCases] = useState<CaseRow[]>(initialCases);
  const [fixtureContext, setFixtureContext] = useState<Awaited<
    ReturnType<typeof loadPortalFixtureContext>
  > | null>(null);
  const [state, setState] = useState<Record<string, CaseState>>(() =>
    buildInitialState(initialCases),
  );
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    void loadPortalFixtureContext().then(setFixtureContext);
  }, []);

  const openCase = openId ? (cases.find((c) => c.id === openId) ?? null) : null;

  const exportPfmeaCsv = () => {
    const headers = [
      "Case",
      "Product",
      "Process",
      "Failure Mode",
      "Effect",
      "D4 Root Cause",
      "Sev Before",
      "Occ Before",
      "Det Before",
      "RPN Before",
      "D5 Corrective",
      "Control Method",
      "Frequency",
      "Reaction Plan",
      "Owner",
      "Sev After",
      "Occ After",
      "Det After",
      "RPN After",
      "RPN Delta",
      "RPN Reduction %",
    ];
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = pfmeaControl.map((r) => {
      const before = r.sevBefore * r.occBefore * r.detBefore;
      const after = r.sevAfter * r.occAfter * r.detAfter;
      return [
        r.caseId,
        r.product,
        r.process,
        r.failureMode,
        r.effect,
        r.cause,
        r.sevBefore,
        r.occBefore,
        r.detBefore,
        before,
        r.corrective,
        r.control,
        r.frequency,
        r.reaction,
        r.owner,
        r.sevAfter,
        r.occAfter,
        r.detAfter,
        after,
        before - after,
        Math.round(((before - after) / before) * 100),
      ]
        .map(esc)
        .join(",");
    });
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pfmea-control-plan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("PFMEA & Control Plan exported as CSV");
  };

  const exportPfmeaPdf = async () => {
    const [{ default: jsPDF }, autoTableMod] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    const autoTable = autoTableMod.default;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("PFMEA & Control Plan — 8D Linkage", 40, 36);
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(
      `Generated ${new Date().toLocaleDateString()} · D4 root cause + D5 corrective action · RPN before vs after`,
      40,
      52,
    );
    autoTable(doc, {
      startY: 64,
      head: [
        [
          "Case",
          "Product / Process",
          "Failure Mode → Effect",
          "D4 Root Cause",
          "S·O·D Before",
          "RPN Before",
          "D5 Corrective / Control",
          "S·O·D After",
          "RPN After",
          "Δ %",
        ],
      ],
      body: pfmeaControl.map((r) => {
        const before = r.sevBefore * r.occBefore * r.detBefore;
        const after = r.sevAfter * r.occAfter * r.detAfter;
        const reduction = Math.round(((before - after) / before) * 100);
        return [
          r.caseId,
          `${r.product}\n${r.process}`,
          `${r.failureMode}\n${r.effect}`,
          r.cause,
          `${r.sevBefore}·${r.occBefore}·${r.detBefore}`,
          String(before),
          `${r.corrective}\n→ ${r.control}\n(${r.frequency})`,
          `${r.sevAfter}·${r.occAfter}·${r.detAfter}`,
          String(after),
          `-${reduction}%`,
        ];
      }),
      styles: { fontSize: 8, cellPadding: 4, valign: "top" },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        4: { halign: "center" },
        5: { halign: "center", textColor: [180, 30, 30], fontStyle: "bold" },
        7: { halign: "center" },
        8: { halign: "center", textColor: [20, 130, 60], fontStyle: "bold" },
        9: { halign: "center", fontStyle: "bold", textColor: [20, 130, 60] },
      },
      didDrawPage: (data) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(140);
        doc.text("Fixture-backed warranty review · illustrative 8D workflow", 40, pageHeight - 16);
        doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.getWidth() - 60, pageHeight - 16);
      },
    });
    doc.save(`pfmea-control-plan-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PFMEA & Control Plan exported as PDF");
  };

  const updateStage = (id: string, code: (typeof D_CODES)[number]) => {
    setCases((prev) =>
      prev.map((c) => (c.id === id ? { ...c, stage: `${code} — ${D_LABELS[code]}` } : c)),
    );
    setState((prev) => {
      const cur = prev[id];
      const idx = D_CODES.indexOf(code);
      const milestones: Record<string, Milestone> = { ...cur.milestones };
      D_CODES.forEach((c, i) => {
        if (i < idx) milestones[c] = { ...milestones[c], done: true };
      });
      return {
        ...prev,
        [id]: {
          milestones,
          log: [
            {
              id: crypto.randomUUID(),
              ts: nowStamp(),
              author: "You",
              text: `Stage advanced to ${code} — ${D_LABELS[code]}`,
              kind: "stage",
            },
            ...cur.log,
          ],
        },
      };
    });
    toast.success(`${id} → ${code} ${D_LABELS[code]}`);
  };

  const toggleMilestone = (id: string, code: (typeof D_CODES)[number]) => {
    setState((prev) => {
      const cur = prev[id];
      const m = cur.milestones[code];
      const next = !m.done;
      return {
        ...prev,
        [id]: {
          milestones: { ...cur.milestones, [code]: { ...m, done: next } },
          log: [
            {
              id: crypto.randomUUID(),
              ts: nowStamp(),
              author: "You",
              text: `Milestone ${code} ${D_LABELS[code]} ${next ? "completed" : "reopened"}`,
              kind: "milestone",
            },
            ...cur.log,
          ],
        },
      };
    });
  };

  const setMilestoneAssignee = (id: string, code: (typeof D_CODES)[number], assignee: string) => {
    setState((prev) => {
      const cur = prev[id];
      const m = cur.milestones[code];
      if (m.assignee === assignee) return prev;
      return {
        ...prev,
        [id]: {
          milestones: { ...cur.milestones, [code]: { ...m, assignee } },
          log: [
            {
              id: crypto.randomUUID(),
              ts: nowStamp(),
              author: "You",
              text: `${code} reassigned to ${assignee}`,
              kind: "assign",
            },
            ...cur.log,
          ],
        },
      };
    });
    toast.success(`${code} assigned to ${assignee}`);
  };

  const setMilestoneDue = (id: string, code: (typeof D_CODES)[number], due: string) => {
    setState((prev) => {
      const cur = prev[id];
      const m = cur.milestones[code];
      return {
        ...prev,
        [id]: {
          milestones: { ...cur.milestones, [code]: { ...m, due } },
          log: [
            {
              id: crypto.randomUUID(),
              ts: nowStamp(),
              author: "You",
              text: `${code} due date set to ${format(new Date(due), "MMM d, yyyy")}`,
              kind: "due",
            },
            ...cur.log,
          ],
        },
      };
    });
  };

  const addNote = (id: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > 500) {
      toast.error("Note must be under 500 characters");
      return;
    }
    setState((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        log: [
          { id: crypto.randomUUID(), ts: nowStamp(), author: "You", text: trimmed, kind: "note" },
          ...prev[id].log,
        ],
      },
    }));
    toast.success("Note logged");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center gap-2 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 xl:flex-none">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0 leading-tight">
              <div className="truncate text-xs font-semibold sm:text-sm">
                Broadview Warranty Intelligence
              </div>
              <div className="hidden text-xs text-muted-foreground sm:block">
                Closed-Loop Quality Engine
              </div>
            </div>
          </div>
          <div className="ml-4 hidden items-center gap-1 rounded-md border border-border bg-input/40 px-3 py-1.5 2xl:flex 2xl:w-80">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              aria-label="Search claims, products, and 8D cases"
              placeholder="Search claim, product, 8D…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <nav aria-label="Primary" className="ml-auto hidden items-center gap-2 xl:flex">
            <span className="hidden items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs text-primary 2xl:inline-flex">
              <CircleDot className="h-3 w-3" />{" "}
              {liveMode ? "Governed API snapshot" : "Fixture snapshot"}
            </span>
            {PORTAL_NAVIGATION_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted",
                  item.to === "/top5" || item.to === "/executive"
                    ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
                    : item.to === "/recall-safety"
                      ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15"
                      : "border-border bg-card",
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-2 xl:ml-2">
            <button className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted">
              Q3 2026
            </button>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">
        <section className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Executive Overview</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {liveMode ? "Broadview Dashboard" : "Synthetic fixture snapshot for human review"}
              {fixtureContext
                ? ` · as of ${new Date(fixtureContext.dashboard.dataAsOf).toLocaleDateString()}`
                : ""}
              {liveMode ? " · governed summary with demo-labelled preview modules." : "."}
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1 text-xs">
            {["7d", "30d", "90d", "YTD"].map((p, i) => (
              <button
                key={p}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  i === 1
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </section>

        {liveMode && (
          <>
            <LiveDashboard embedded />
          </>
        )}

        <section
          className="rounded-xl border border-primary/30 p-5"
          style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold">Top 5 Intelligence</h2>
                <p className="text-xs text-muted-foreground">
                  Ranked problems, components, products, car lines, suppliers and failure modes —
                  drill into claim evidence and open the Problem Record.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs lg:ml-auto">
              <div className="rounded-lg border border-border bg-background px-3 py-2">
                <div className="text-muted-foreground">#1 Problem</div>
                <div className="font-semibold">HV Battery thermal shutdown</div>
              </div>
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                <div className="opacity-80">Exposure</div>
                <div className="font-semibold">$3.84M · 412 claims</div>
              </div>
              <Link
                to="/top5"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Open Top 5 <Trophy className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/problem-records"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 py-2.5 text-xs font-semibold hover:bg-muted"
              >
                Problem Record Registry
              </Link>
            </div>
          </div>
        </section>

        {!liveMode && (
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k, i) => {
              const Icon = [Boxes, Gauge, TrendingDown, Brain][i];
              const positive = k.trend === "down";
              return (
                <div
                  key={k.label}
                  className="relative overflow-hidden rounded-xl border border-border p-5"
                  style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span
                      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                        positive ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                      }`}
                    >
                      {k.trend === "down" ? (
                        <ArrowDownRight className="h-3 w-3" />
                      ) : (
                        <ArrowUpRight className="h-3 w-3" />
                      )}
                      {k.delta}
                    </span>
                  </div>
                  <div className="mt-4 text-3xl font-semibold tracking-tight">{k.value}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{k.label}</div>
                  <div className="mt-3 text-xs text-muted-foreground/80">{k.hint}</div>
                </div>
              );
            })}
          </section>
        )}

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ClaimsVolumeTrend />

          <HistoricalFailureModes />
        </section>

        <WarrantyCostForecastSection />

        <ClaimsPerThousandSection />

        <ClaimsControlChartSection />

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div
            className="rounded-xl border border-border p-5 lg:col-span-2"
            style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" /> 8D Resolution Pipeline
                </h2>
                <p className="text-xs text-muted-foreground">Active cases by discipline stage</p>
              </div>
              <span className="text-xs text-muted-foreground">301 active</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={eightDStages} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid
                    stroke="var(--color-border)"
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="code"
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
                    contentStyle={{
                      background: "var(--color-popover)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: number, _n, p) => [`${v} cases`, p.payload.label]}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="var(--color-primary)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-[11px] text-muted-foreground sm:grid-cols-8">
              {eightDStages.map((s) => (
                <div
                  key={s.code}
                  className="rounded-md border border-border/60 bg-background/40 px-1 py-1.5"
                >
                  <div className="font-semibold text-foreground">{s.code}</div>
                  <div className="truncate">{s.label}</div>
                </div>
              ))}
            </div>
            <ChartDataTable
              chartTitle="8D Resolution Pipeline"
              columns={[
                { key: "code", label: "Stage" },
                { key: "label", label: "Discipline" },
                { key: "count", label: "Active cases", unit: "cases", numeric: true },
              ]}
              rows={eightDStages.map((stage) => ({
                code: stage.code,
                label: stage.label,
                count: stage.count,
              }))}
              summary={`${eightDStages.reduce((total, stage) => total + stage.count, 0)} active cases across ${eightDStages.length} discipline stages.`}
              contextNote="Synthetic fixture counts for human review; not a live pipeline or a production case registry."
            />
          </div>

          <div
            className="rounded-xl border border-border p-5"
            style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
          >
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" /> Fixture Risk Signals
            </h2>
            <p className="text-xs text-muted-foreground">
              Synthetic examples for human review; not a live alerting or decision service
            </p>
            <ul className="mt-4 space-y-3">
              {predictiveAlerts.map((a) => (
                <li
                  key={a.product}
                  className="rounded-lg border border-border/60 bg-background/40 p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{a.product}</span>
                    <span
                      className={`text-xs font-semibold ${
                        a.risk >= 75
                          ? "text-destructive"
                          : a.risk >= 55
                            ? "text-warning"
                            : "text-primary"
                      }`}
                    >
                      {a.risk}% risk
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${a.risk}%`,
                        background:
                          a.risk >= 75
                            ? "var(--color-destructive)"
                            : a.risk >= 55
                              ? "var(--color-warning)"
                              : "var(--color-primary)",
                      }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{a.driver}</span>
                    <span>
                      {a.window} · {a.units}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className="overflow-hidden rounded-xl border border-border"
          style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Layers className="h-4 w-4 text-accent" /> PFMEA & Control Plan — linked to 8D
              </h2>
              <p className="text-xs text-muted-foreground">
                D4 root causes feed PFMEA scoring · D5 corrective actions drive Control Plan methods
                · RPN tracked before vs after
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden gap-3 text-[10px] uppercase tracking-wide text-muted-foreground lg:flex">
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-destructive" /> RPN before
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-success" /> RPN after
                </span>
              </div>
              <button
                onClick={exportPfmeaCsv}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <Download className="h-3 w-3" /> CSV
              </button>
              <button
                onClick={exportPfmeaPdf}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <FileText className="h-3 w-3" /> PDF
              </button>
            </div>
          </div>
          <div
            tabIndex={0}
            aria-label="PFMEA and Control Plan table. Use the arrow keys or Shift plus mouse wheel to view additional columns."
            className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            <table className="w-full text-sm">
              <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 text-left font-medium">Case / Process</th>
                  <th className="px-5 py-3 text-left font-medium">Failure Mode → Effect</th>
                  <th className="px-5 py-3 text-left font-medium">D4 Root Cause</th>
                  <th className="px-5 py-3 text-center font-medium">S·O·D</th>
                  <th className="px-5 py-3 text-left font-medium">D5 Corrective → Control</th>
                  <th className="px-5 py-3 text-left font-medium">Frequency / Reaction</th>
                  <th className="px-5 py-3 text-right font-medium">RPN Δ</th>
                </tr>
              </thead>
              <tbody>
                {pfmeaControl.map((r) => {
                  const rpnBefore = r.sevBefore * r.occBefore * r.detBefore;
                  const rpnAfter = r.sevAfter * r.occAfter * r.detAfter;
                  const reduction = Math.round(((rpnBefore - rpnAfter) / rpnBefore) * 100);
                  return (
                    <tr
                      key={r.caseId}
                      className="border-t border-border/60 align-top transition-colors hover:bg-background/40"
                    >
                      <td className="px-5 py-3">
                        <div className="font-mono text-xs text-primary">{r.caseId}</div>
                        <div className="font-medium">{r.product}</div>
                        <div className="text-xs text-muted-foreground">{r.process}</div>
                      </td>
                      <td className="px-5 py-3 max-w-[220px]">
                        <div className="font-medium">{r.failureMode}</div>
                        <div className="text-xs text-muted-foreground">{r.effect}</div>
                      </td>
                      <td className="px-5 py-3 max-w-[220px] text-xs">
                        <span className="mb-1 inline-block rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary">
                          D4
                        </span>
                        <div>{r.cause}</div>
                      </td>
                      <td className="px-5 py-3 text-center text-xs">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-mono text-destructive">
                            {r.sevBefore}·{r.occBefore}·{r.detBefore}
                          </span>
                          <span className="text-[10px] text-muted-foreground">↓</span>
                          <span className="font-mono text-success">
                            {r.sevAfter}·{r.occAfter}·{r.detAfter}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 max-w-[240px] text-xs">
                        <span className="mb-1 inline-block rounded bg-accent/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-accent">
                          D5
                        </span>
                        <div className="mb-1.5">{r.corrective}</div>
                        <div className="flex items-start gap-1.5 rounded-md bg-background/50 px-2 py-1.5">
                          <ClipboardCheck className="mt-0.5 h-3 w-3 shrink-0 text-success" />
                          <span>{r.control}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Gauge className="h-3 w-3" /> {r.frequency}
                        </div>
                        <div className="mt-1 flex items-start gap-1.5">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
                          <span>{r.reaction}</span>
                        </div>
                        <div className="mt-1 text-[10px]">Owner · {r.owner}</div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="font-mono text-xs text-destructive line-through">
                          {rpnBefore}
                        </div>
                        <div className="font-mono text-sm font-semibold text-success">
                          {rpnAfter}
                        </div>
                        <div className="mt-0.5 inline-flex items-center gap-0.5 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                          <TrendingDown className="h-2.5 w-2.5" /> {reduction}%
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border/60 bg-background/30 px-5 py-3 text-[11px] text-muted-foreground">
            Closed loop: 8D D4 findings update PFMEA cause &amp; occurrence rating · D5 corrective
            actions become Control Plan entries · post-implementation RPN informs the fixture
            scenario model.
          </div>
        </section>

        <ReadAcrossSection />

        <AuditTrailSection />

        <section
          className="overflow-hidden rounded-xl border border-border"
          style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-primary" /> Active 8D Cases
              </h2>
              <p className="text-xs text-muted-foreground">
                Manage stage progression, log notes, and complete milestones
              </p>
            </div>
            <button className="rounded-md border border-border bg-background/60 px-3 py-1.5 text-xs font-medium hover:bg-muted">
              View all
            </button>
          </div>
          <div
            tabIndex={0}
            aria-label="Active 8D Cases table. Use the arrow keys or Shift plus mouse wheel to view additional columns."
            className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            <table className="w-full text-sm">
              <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 text-left font-medium">Case</th>
                  <th className="px-5 py-3 text-left font-medium">Product / Issue</th>
                  <th className="px-5 py-3 text-left font-medium">8D Stage</th>
                  <th className="px-5 py-3 text-left font-medium">Owner</th>
                  <th className="px-5 py-3 text-left font-medium">Next Due</th>
                  <th className="px-5 py-3 text-left font-medium">Progress</th>
                  <th className="px-5 py-3 text-left font-medium">Status</th>
                  <th className="px-5 py-3 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => {
                  const cs = state[c.id];
                  const completed = D_CODES.filter((k) => cs.milestones[k].done).length;
                  const overdue = countOverdue(cs);
                  const next = D_CODES.map((k) => cs.milestones[k]).find((m) => !m.done);
                  const nextOverdue = next && isOverdue(next);
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-border/60 transition-colors hover:bg-background/40"
                    >
                      <td className="px-5 py-3 font-mono text-xs text-primary align-top">{c.id}</td>
                      <td className="px-5 py-3 align-top">
                        <div className="font-medium">{c.product}</div>
                        <div className="text-xs text-muted-foreground">{c.issue}</div>
                      </td>
                      <td className="px-5 py-3 align-top">
                        <div>{c.stage}</div>
                        {overdue > 0 && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                            <Flame className="h-2.5 w-2.5" /> {overdue} overdue
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-top">
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                            {initials(c.owner)}
                          </span>
                          {c.owner}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-top">
                        {next ? (
                          <div
                            className={cn(
                              "text-xs",
                              nextOverdue
                                ? "text-destructive font-medium"
                                : "text-muted-foreground",
                            )}
                          >
                            <div>{format(new Date(next.due + "T00:00:00"), "MMM d")}</div>
                            <div className="text-[10px] opacity-80">{next.assignee}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-success">Closed</span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                overdue > 0 ? "bg-destructive" : "bg-primary",
                              )}
                              style={{ width: `${(completed / 8) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{completed}/8</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 align-top">
                        {overdue > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                            <AlertTriangle className="h-3 w-3" /> Overdue
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusTone[c.status]}`}
                          >
                            {c.status === "On Track" && <Activity className="h-3 w-3" />}
                            {c.status}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right align-top">
                        <button
                          onClick={() => setOpenId(c.id)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
                        >
                          <Settings2 className="h-3 w-3" /> Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="pb-6 pt-2 text-center text-xs text-muted-foreground">
          Fixture-backed warranty review · illustrative 8D methodology
        </footer>
      </main>

      {openCase && (
        <CaseWorkflowDialog
          caseRow={openCase}
          state={state[openCase.id]}
          onClose={() => setOpenId(null)}
          onStageChange={(code) => updateStage(openCase.id, code)}
          onMilestone={(code) => toggleMilestone(openCase.id, code)}
          onAssign={(code, who) => setMilestoneAssignee(openCase.id, code, who)}
          onDue={(code, iso) => setMilestoneDue(openCase.id, code, iso)}
          onNote={(text) => addNote(openCase.id, text)}
        />
      )}
    </div>
  );
}

function CaseWorkflowDialog({
  caseRow,
  state,
  onClose,
  onStageChange,
  onMilestone,
  onAssign,
  onDue,
  onNote,
}: {
  caseRow: CaseRow;
  state: CaseState;
  onClose: () => void;
  onStageChange: (code: (typeof D_CODES)[number]) => void;
  onMilestone: (code: (typeof D_CODES)[number]) => void;
  onAssign: (code: (typeof D_CODES)[number], assignee: string) => void;
  onDue: (code: (typeof D_CODES)[number], iso: string) => void;
  onNote: (text: string) => void;
}) {
  const [note, setNote] = useState("");
  const currentCode = caseRow.stage.split(" ")[0];
  const completed = useMemo(
    () => D_CODES.filter((c) => state.milestones[c].done).length,
    [state.milestones],
  );
  const overdue = useMemo(() => countOverdue(state), [state]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-border shadow-2xl"
        style={{ background: "var(--gradient-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono text-primary">{caseRow.id}</span>
              <span>·</span>
              <span>{caseRow.product}</span>
              <span
                className={`ml-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${severityTone[caseRow.severity]}`}
              >
                {caseRow.severity}
              </span>
              {overdue > 0 && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                  <Flame className="h-3 w-3" /> {overdue} overdue
                </span>
              )}
            </div>
            <h3 className="mt-1 text-lg font-semibold">{caseRow.issue}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Owner {caseRow.owner} · Currently at{" "}
              <span className="text-foreground">{caseRow.stage}</span> · {completed}/8 milestones
              complete
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid max-h-[calc(90vh-92px)] grid-cols-1 gap-5 overflow-y-auto p-5 md:grid-cols-5">
          <div className="space-y-5 md:col-span-3">
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Update 8D stage
              </h4>
              <div className="grid grid-cols-4 gap-1.5">
                {D_CODES.map((c) => {
                  const active = c === currentCode;
                  return (
                    <button
                      key={c}
                      onClick={() => onStageChange(c)}
                      className={`rounded-md border px-2 py-2 text-center transition-all ${
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow-[var(--shadow-glow)]"
                          : "border-border bg-background/40 hover:border-primary/50 hover:bg-primary/10"
                      }`}
                    >
                      <div className="text-xs font-bold">{c}</div>
                      <div className="text-[10px] opacity-80">{D_LABELS[c]}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Milestones · assignees · due dates
                </h4>
                <span className="text-xs text-muted-foreground">{completed}/8 done</span>
              </div>
              <ul className="space-y-1.5">
                {D_CODES.map((c) => {
                  const m = state.milestones[c];
                  const od = isOverdue(m);
                  return (
                    <li
                      key={c}
                      className={cn(
                        "flex flex-wrap items-center gap-2 rounded-md border bg-background/40 px-2.5 py-2 transition-colors",
                        od ? "border-destructive/50 bg-destructive/5" : "border-border/60",
                      )}
                    >
                      <button
                        onClick={() => onMilestone(c)}
                        className="group flex shrink-0 items-center gap-2"
                        aria-label={`Toggle ${c}`}
                      >
                        <span
                          className={cn(
                            "flex h-5 w-5 items-center justify-center rounded border transition-colors",
                            m.done
                              ? "border-success bg-success text-background"
                              : "border-border bg-background group-hover:border-primary",
                          )}
                        >
                          {m.done && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="font-mono text-xs font-bold text-primary">{c}</span>
                        <span
                          className={cn(
                            "text-sm",
                            m.done ? "text-muted-foreground line-through" : "text-foreground",
                          )}
                        >
                          {D_LABELS[c]}
                        </span>
                      </button>

                      <div className="ml-auto flex flex-wrap items-center gap-1.5">
                        <AssigneeSelect value={m.assignee} onChange={(v) => onAssign(c, v)} />
                        <DueDatePicker value={m.due} overdue={od} onChange={(d) => onDue(c, d)} />
                        {od && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                            <AlertTriangle className="h-2.5 w-2.5" /> Overdue
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="flex min-h-[400px] flex-col md:col-span-2">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Log a note
            </h4>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. Containment: 1,240 units quarantined at DC-3."
              className="w-full resize-none rounded-md border border-border bg-input/40 px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{note.length}/500</span>
              <button
                onClick={() => {
                  onNote(note);
                  setNote("");
                }}
                disabled={!note.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                <MessageSquarePlus className="h-3.5 w-3.5" /> Add to log
              </button>
            </div>

            <h4 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Activity
            </h4>
            <ol className="flex-1 space-y-2.5 overflow-y-auto pr-1">
              {state.log.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <span
                    className={cn(
                      "mt-1 h-2 w-2 shrink-0 rounded-full",
                      e.kind === "stage" && "bg-primary",
                      e.kind === "milestone" && "bg-success",
                      e.kind === "assign" && "bg-accent",
                      e.kind === "due" && "bg-warning",
                      e.kind === "note" && "bg-muted-foreground",
                    )}
                  />
                  <div className="flex-1 rounded-md border border-border/60 bg-background/40 px-3 py-2">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">{e.author}</span>
                      <span>{e.ts}</span>
                    </div>
                    <p className="mt-0.5 text-sm">{e.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssigneeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <UserRound className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer appearance-none rounded-md border border-border bg-background/60 py-1 pl-6 pr-2 text-xs text-foreground outline-none hover:border-primary/50 focus:border-primary"
      >
        {teamMembers.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}

function DueDatePicker({
  value,
  overdue,
  onChange,
}: {
  value: string;
  overdue: boolean;
  onChange: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const date = new Date(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border bg-background/60 px-2 py-1 text-xs transition-colors hover:border-primary/50",
            overdue
              ? "border-destructive/50 text-destructive"
              : "border-border text-muted-foreground",
          )}
        >
          <CalendarIcon className="h-3 w-3" />
          {format(date, "MMM d")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            if (d) {
              onChange(isoDay(d));
              setOpen(false);
            }
          }}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}

function initials(name: string) {
  return name
    .split(/[\s.]+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase())
    .slice(0, 2)
    .join("");
}

function nowStamp() {
  const d = new Date();
  return `Today · ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function Legend({ dot, label, dashed }: { dot: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <span
        className="h-0.5 w-4 rounded-full"
        style={{
          background: dashed
            ? `repeating-linear-gradient(90deg, ${dot} 0 4px, transparent 4px 8px)`
            : dot,
        }}
      />
      {label}
    </span>
  );
}

const readAcrossStatusTone: Record<ReadAcrossStatus, string> = {
  Applied: "bg-success/15 text-success border-success/30",
  "In Review": "bg-warning/15 text-warning border-warning/30",
  Open: "bg-destructive/15 text-destructive border-destructive/30",
  "Not Applicable": "bg-muted text-muted-foreground border-border",
};

const READ_ACROSS_CHECKLIST = [
  "Review source 8D D4 root cause & D5 corrective with target program team",
  "Update receiving program PFMEA: cause, occurrence, detection rating",
  "Add control method to receiving Control Plan + define reaction plan",
  "Pilot on next production lot / firmware build and verify effectiveness",
  "Close-out: update lessons-learned database & notify stakeholders",
];

function WarrantyCostForecastSection() {
  const stats = useMemo(() => {
    const lastActual = [...warrantyCostForecast].reverse().find((d) => d.actual !== null);
    const lastForecast = warrantyCostForecast[warrantyCostForecast.length - 1];
    const firstActual = warrantyCostForecast.find((d) => d.actual !== null);
    const trend =
      lastActual && firstActual && firstActual.actual && lastActual.actual
        ? ((lastActual.actual - firstActual.actual) / firstActual.actual) * 100
        : 0;
    const projectedSavings = warrantyCostForecast
      .filter((d) => d.actual === null)
      .reduce((sum, d) => sum + Math.max(0, d.budget - d.forecast), 0);
    return {
      currentActual: lastActual?.actual ?? 0,
      endForecast: lastForecast.forecast,
      trendPct: trend,
      projectedSavings,
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {warrantyCostByLine.map((line) => {
          const actuals = line.series.filter((p) => p.actual !== null) as {
            month: string;
            actual: number;
            forecast: number;
          }[];
          const lastActual = actuals[actuals.length - 1];
          const firstActual = actuals[0];
          const deltaPct =
            firstActual && lastActual
              ? ((lastActual.actual - firstActual.actual) / firstActual.actual) * 100
              : 0;
          const lastForecast = line.series[line.series.length - 1].forecast;
          const aboveForecast = lastActual && lastActual.actual > lastActual.forecast;
          return (
            <div
              key={line.key}
              className="rounded-xl border border-border p-3"
              style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-foreground">{line.label}</span>
                {aboveForecast ? (
                  <ArrowUpRight className="h-3 w-3 text-warning" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-success" />
                )}
              </div>
              <div className="h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={line.series} margin={{ top: 4, bottom: 2, left: 2, right: 2 }}>
                    <Tooltip
                      cursor={{
                        stroke: "var(--color-muted-foreground)",
                        strokeWidth: 1,
                        strokeDasharray: "2 2",
                      }}
                      contentStyle={{
                        background: "var(--color-popover)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 8,
                        fontSize: 11,
                        padding: "6px 8px",
                      }}
                      labelStyle={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--color-foreground)",
                      }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const p = payload[0].payload as { actual: number | null; forecast: number };
                        const delta =
                          p.actual !== null ? ((p.actual - p.forecast) / p.forecast) * 100 : null;
                        return (
                          <div className="rounded-md border border-border bg-popover px-2 py-1.5 text-[11px] shadow-md">
                            <div className="mb-1 font-semibold text-foreground">{label}</div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-muted-foreground">Actual</span>
                              <span className="font-mono font-medium text-foreground">
                                {p.actual !== null ? `$${p.actual.toFixed(2)}M` : "—"}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-muted-foreground">Forecast</span>
                              <span className="font-mono font-medium text-accent">
                                ${p.forecast.toFixed(2)}M
                              </span>
                            </div>
                            <div className="mt-0.5 flex items-center justify-between gap-3 border-t border-border/60 pt-1">
                              <span className="text-muted-foreground">Δ vs forecast</span>
                              <span
                                className={cn(
                                  "font-mono font-medium",
                                  delta === null
                                    ? "text-muted-foreground"
                                    : delta < 0
                                      ? "text-success"
                                      : "text-warning",
                                )}
                              >
                                {delta === null
                                  ? "—"
                                  : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%`}
                              </span>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="var(--color-primary)"
                      strokeWidth={1.75}
                      dot={false}
                      activeDot={{ r: 3, stroke: "var(--color-background)", strokeWidth: 1 }}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecast"
                      stroke="var(--color-accent)"
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      dot={false}
                      activeDot={{ r: 3, stroke: "var(--color-background)", strokeWidth: 1 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-1 flex items-baseline justify-between text-[11px]">
                <span className="font-mono font-semibold text-foreground">
                  ${lastActual?.actual.toFixed(2)}M
                </span>
                <span className="text-muted-foreground">
                  fc ${lastForecast.toFixed(2)}M
                  <span className={cn("ml-1.5", deltaPct < 0 ? "text-success" : "text-warning")}>
                    {deltaPct > 0 ? "+" : ""}
                    {deltaPct.toFixed(1)}%
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div
          className="rounded-xl border border-border p-5 lg:col-span-3"
          style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" /> Warranty Cost Fixture Scenario
              </h2>
              <p className="text-xs text-muted-foreground">
                Synthetic warranty-cost example ($M) with illustrative scenario bounds and budget
                reference
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Legend dot="var(--color-primary)" label="Fixture measure" />
              <Legend dot="var(--color-accent)" label="Fixture scenario" dashed />
              <Legend dot="var(--color-muted-foreground)" label="Illustrative range" />
              <Legend dot="var(--color-warning)" label="Budget reference" dashed />
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={warrantyCostForecast} margin={{ left: -10, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="var(--color-border)"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v.toFixed(1)}M`}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-popover)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: unknown, name) => {
                    if (value === null || value === undefined) return ["—", name as string];
                    return [`$${Number(value).toFixed(2)}M`, name as string];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="upper"
                  stroke="none"
                  fill="var(--color-muted-foreground)"
                  fillOpacity={0.08}
                  name="Upper bound"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="lower"
                  stroke="none"
                  fill="var(--color-background)"
                  fillOpacity={1}
                  name="Lower bound"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="actual"
                  stroke="var(--color-primary)"
                  fill="url(#gActual)"
                  strokeWidth={2.5}
                  name="Fixture measure"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  stroke="var(--color-accent)"
                  strokeDasharray="5 4"
                  strokeWidth={2}
                  dot={false}
                  name="Fixture scenario"
                />
                <Line
                  type="monotone"
                  dataKey="budget"
                  stroke="var(--color-warning)"
                  strokeDasharray="2 4"
                  strokeWidth={1.5}
                  dot={false}
                  name="Budget reference"
                />
                <ReferenceLine
                  x="May '26"
                  stroke="var(--color-muted-foreground)"
                  strokeDasharray="2 2"
                  label={{
                    value: "Today",
                    position: "top",
                    fill: "var(--color-muted-foreground)",
                    fontSize: 10,
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <ChartDataTable
            chartTitle="Warranty Cost Fixture Scenario"
            columns={[
              { key: "month", label: "Month" },
              { key: "actual", label: "Fixture measure", unit: "USD millions", numeric: true },
              { key: "forecast", label: "Scenario", unit: "USD millions", numeric: true },
              { key: "lower", label: "Lower bound", unit: "USD millions", numeric: true },
              { key: "upper", label: "Upper bound", unit: "USD millions", numeric: true },
              { key: "budget", label: "Budget reference", unit: "USD millions", numeric: true },
            ]}
            rows={warrantyCostForecast.map((point) => ({
              month: point.month,
              actual: point.actual === null ? null : `$${point.actual.toFixed(2)}M`,
              forecast: `$${point.forecast.toFixed(2)}M`,
              lower: `$${point.lower.toFixed(2)}M`,
              upper: `$${point.upper.toFixed(2)}M`,
              budget: `$${point.budget.toFixed(2)}M`,
            }))}
            summary={`${warrantyCostForecast.length} monthly points; the fixture measure is not shown for months without a synthetic actual.`}
            contextNote="Illustrative synthetic scenario only. Production calculation, source data, quality state, and approval authority will be supplied through the coordinator-owned API contract."
          />
        </div>

        <div className="flex flex-col gap-3">
          <ForecastStat
            label="Fixture Measure (MTD)"
            value={`$${stats.currentActual.toFixed(2)}M`}
            hint="Synthetic input · Estimated Exposure"
            tone="primary"
          />
          <ForecastStat
            label="12-mo Fixture Scenario"
            value={`$${stats.endForecast.toFixed(2)}M`}
            hint={`Illustrative October scenario (${stats.trendPct.toFixed(1)}% YoY)`}
            tone="accent"
          />
          <ForecastStat
            label="Potential Avoided Exposure"
            value={`$${stats.projectedSavings.toFixed(2)}M`}
            hint="Synthetic scenario; not approved savings or a budget outcome"
            tone="success"
          />
          <div className="rounded-xl border border-border/60 bg-background/40 p-4 text-[11px] text-muted-foreground">
            <p className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
              <Sparkles className="h-3 w-3 text-accent" /> Fixture method note
            </p>
            Illustrative synthetic scenario only. Production calculation, source data, quality
            state, and approval authority will be supplied through the coordinator-owned API
            contract.
          </div>
        </div>
      </section>
    </div>
  );
}

function ForecastStat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone: "primary" | "accent" | "success" | "warning";
}) {
  const toneClass = {
    primary: "text-primary",
    accent: "text-accent",
    success: "text-success",
    warning: "text-warning",
  }[tone];
  return (
    <div
      className="rounded-xl border border-border p-4"
      style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
    >
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-2xl font-semibold", toneClass)}>{value}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function ClaimsPerThousandSection() {
  const stats = useMemo(() => {
    const at12 = claimsPerThousand.find((d) => d.mis === 12)!;
    const target12 = at12.target;
    const worstProgram =
      at12.evx >= at12.hybrid && at12.evx >= at12.ice
        ? { name: "EV-X Platform", value: at12.evx }
        : at12.hybrid >= at12.ice
          ? { name: "Hybrid Line", value: at12.hybrid }
          : { name: "ICE Legacy", value: at12.ice };
    const bestProgram = { name: "ICE Legacy", value: at12.ice };
    const fleetLast = [...claimsPerThousand].reverse().find((d) => d.fleet !== null);
    const fleetProj = fleetLast?.fleet ?? 0;
    const gapToTarget = worstProgram.value - target12;
    return { worstProgram, bestProgram, fleetProj, gapToTarget, target12 };
  }, []);

  return (
    <section className="grid grid-cols-1 gap-4 lg:grid-cols-4">
      <div
        className="rounded-xl border border-border p-5 lg:col-span-3"
        style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Gauge className="h-4 w-4 text-primary" /> Claims per 1,000 Vehicles (C/1000)
            </h2>
            <p className="text-xs text-muted-foreground">
              Cumulative claim rate by Months In Service (MIS) — tracked against IATF reliability
              target and warning threshold
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Legend dot="var(--color-primary)" label="EV-X Platform" />
            <Legend dot="var(--color-accent)" label="Hybrid Line" />
            <Legend dot="var(--color-success)" label="ICE Legacy" />
            <Legend dot="var(--color-warning)" label="Active Fleet" dashed />
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={claimsPerThousand} margin={{ left: -10, right: 8, top: 8 }}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="mis"
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `MIS ${v}`}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}`}
                label={{
                  value: "C/1000",
                  angle: -90,
                  position: "insideLeft",
                  fill: "var(--color-muted-foreground)",
                  fontSize: 11,
                  offset: 18,
                }}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--color-popover)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelFormatter={(v) => `Months in Service: ${v}`}
                formatter={(value: unknown, name) => {
                  if (value === null || value === undefined) return ["—", name as string];
                  return [`${Number(value).toFixed(1)} C/1000`, name as string];
                }}
              />
              <Area
                type="monotone"
                dataKey="warning"
                stroke="none"
                fill="var(--color-destructive)"
                fillOpacity={0.06}
                name="Warning zone"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="var(--color-muted-foreground)"
                strokeDasharray="2 4"
                strokeWidth={1.5}
                dot={false}
                name="Reliability target"
              />
              <Line
                type="monotone"
                dataKey="evx"
                stroke="var(--color-primary)"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                name="EV-X Platform"
              />
              <Line
                type="monotone"
                dataKey="hybrid"
                stroke="var(--color-accent)"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Hybrid Line"
              />
              <Line
                type="monotone"
                dataKey="ice"
                stroke="var(--color-success)"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="ICE Legacy"
              />
              <Line
                type="monotone"
                dataKey="fleet"
                stroke="var(--color-warning)"
                strokeDasharray="5 4"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Active Fleet (fixture)"
                connectNulls={false}
              />
              <ReferenceLine
                y={12}
                stroke="var(--color-muted-foreground)"
                strokeDasharray="2 2"
                label={{
                  value: "Target 12",
                  position: "right",
                  fill: "var(--color-muted-foreground)",
                  fontSize: 10,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <ChartDataTable
          chartTitle="Claims per 1,000 Vehicles (C/1000)"
          columns={[
            { key: "mis", label: "Months in service", numeric: true },
            { key: "evx", label: "EV-X Platform", numeric: true },
            { key: "hybrid", label: "Hybrid Line", numeric: true },
            { key: "ice", label: "ICE Legacy", numeric: true },
            { key: "fleet", label: "Active Fleet (fixture)", numeric: true },
            { key: "target", label: "IATF target", numeric: true },
            { key: "warning", label: "Warning threshold", numeric: true },
          ]}
          rows={claimsPerThousand.map((point) => ({
            mis: point.mis,
            evx: point.evx.toFixed(1),
            hybrid: point.hybrid.toFixed(1),
            ice: point.ice.toFixed(1),
            fleet: point.fleet === null ? null : point.fleet.toFixed(1),
            target: point.target,
            warning: point.warning,
          }))}
          summary={`${claimsPerThousand.length} months-in-service points; cumulative claim rate per 1,000 vehicles against the IATF reliability target of 12, with the plotted warning-threshold zone (ranging 4-18 by months in service) included so the table equals the full plotted data.`}
          contextNote="Synthetic fixture source — not telematics-fed. Use for reliability-pattern review only; not a production vehicle-fleet measure."
        />
      </div>

      <div className="flex flex-col gap-3">
        <ForecastStat
          label="Worst Program @ MIS 12"
          value={`${stats.worstProgram.value.toFixed(1)}`}
          hint={`${stats.worstProgram.name} — ${stats.gapToTarget.toFixed(1)} above target`}
          tone="primary"
        />
        <ForecastStat
          label="Best Program @ MIS 12"
          value={`${stats.bestProgram.value.toFixed(1)}`}
          hint={`${stats.bestProgram.name} — within target ${stats.target12}`}
          tone="success"
        />
        <ForecastStat
          label="Active Fleet (fixture)"
          value={`${stats.fleetProj.toFixed(1)}`}
          hint="Synthetic fixture source — not telematics-fed"
          tone="accent"
        />
        <div className="rounded-xl border border-border/60 bg-background/40 p-4 text-[11px] text-muted-foreground">
          <p className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
            <Sparkles className="h-3 w-3 text-accent" /> Reliability insight
          </p>
          EV-X breaches the warning threshold at MIS 8 — driven by HV-battery & inverter claims now
          under 8D-2451. Read-across to Hybrid Line projected to hold curve below target through MIS
          12.
        </div>
      </div>
    </section>
  );
}

function ClaimsControlChartSection() {
  const [activeKey, setActiveKey] = useState(claimsControlChart[0].key);
  const component = claimsControlChart.find((c) => c.key === activeKey)!;

  const { data, cl, ucl, lcl, sigma, violations, lastValue, inControl } = useMemo(() => {
    const series = component.series;
    const values = series.map((p) => p.claims);
    const cl = values.reduce((a, b) => a + b, 0) / values.length;
    // Moving range (n=2) → σ ≈ MR̄ / 1.128
    const mr: number[] = [];
    for (let i = 1; i < values.length; i++) mr.push(Math.abs(values[i] - values[i - 1]));
    const mrBar = mr.reduce((a, b) => a + b, 0) / mr.length;
    const sigma = mrBar / 1.128;
    const ucl = cl + 3 * sigma;
    const lcl = Math.max(0, cl - 3 * sigma);
    const data = series.map((p) => ({
      ...p,
      cl,
      ucl,
      lcl,
      violation: p.claims > ucl || p.claims < lcl ? p.claims : null,
    }));
    const violations = data.filter((d) => d.violation !== null).length;
    const lastValue = values[values.length - 1];
    const inControl = lastValue <= ucl && lastValue >= lcl;
    return { data, cl, ucl, lcl, sigma, violations, lastValue, inControl };
  }, [component]);

  return (
    <section className="grid grid-cols-1 gap-4 lg:grid-cols-4">
      <div
        className="rounded-xl border border-border p-5 lg:col-span-3"
        style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" /> Claims SPC Control Chart — by Component
            </h2>
            <p className="text-xs text-muted-foreground">
              Monthly claim counts with ±3σ control limits (Shewhart, MR/1.128). Points outside
              UCL/LCL flag special-cause variation per IATF 16949 §9.1.1.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Legend dot="var(--color-primary)" label="Claims" />
            <Legend dot="var(--color-muted-foreground)" label="Center line (CL)" dashed />
            <Legend dot="var(--color-destructive)" label="UCL / LCL ±3σ" dashed />
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {claimsControlChart.map((c) => {
            const vals = c.series.map((p) => p.claims);
            const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
            const mr: number[] = [];
            for (let i = 1; i < vals.length; i++) mr.push(Math.abs(vals[i] - vals[i - 1]));
            const sig = mr.reduce((a, b) => a + b, 0) / mr.length / 1.128;
            const ucl = mean + 3 * sig;
            const lcl = Math.max(0, mean - 3 * sig);
            const last = vals[vals.length - 1];
            const prev = vals[vals.length - 2] ?? last;
            const delta = last - prev;
            const breached = c.series.some((p) => p.claims > ucl || p.claims < lcl);
            const isActive = activeKey === c.key;
            const stroke = breached ? "var(--color-destructive)" : "var(--color-primary)";
            return (
              <button
                key={c.key}
                onClick={() => setActiveKey(c.key)}
                className={cn(
                  "group flex flex-col gap-1 rounded-lg border p-2.5 text-left transition-all cursor-pointer",
                  isActive
                    ? "border-primary/60 bg-primary/10 shadow-sm"
                    : "border-border bg-background/40 hover:border-primary/30 hover:bg-background/60",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[11px] font-medium leading-tight text-foreground">
                    {c.label}
                  </span>
                  {breached && <AlertTriangle className="h-3 w-3 shrink-0 text-destructive" />}
                </div>
                <div className="-mx-1 h-10">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={c.series} margin={{ top: 4, bottom: 2, left: 2, right: 2 }}>
                      <Line
                        type="monotone"
                        dataKey="claims"
                        stroke={stroke}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                    {last}
                  </span>
                  <span
                    className={cn(
                      "flex items-center gap-0.5 text-[10px] font-medium tabular-nums",
                      delta > 0
                        ? "text-destructive"
                        : delta < 0
                          ? "text-emerald-500"
                          : "text-muted-foreground",
                    )}
                  >
                    {delta > 0 ? (
                      <ArrowUpRight className="h-2.5 w-2.5" />
                    ) : delta < 0 ? (
                      <ArrowDownRight className="h-2.5 w-2.5" />
                    ) : null}
                    {delta > 0 ? "+" : ""}
                    {delta}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ left: -10, right: 16, top: 8 }}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--color-popover)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: unknown, name) => {
                  if (value === null || value === undefined) return ["—", name as string];
                  return [`${Number(value).toFixed(1)}`, name as string];
                }}
              />
              <Area
                type="monotone"
                dataKey={() => ucl - lcl}
                stackId="band"
                stroke="none"
                fill="transparent"
                isAnimationActive={false}
                name=""
              />
              <Line
                type="monotone"
                dataKey="ucl"
                stroke="var(--color-destructive)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
                name="UCL (+3σ)"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="lcl"
                stroke="var(--color-destructive)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
                name="LCL (−3σ)"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="cl"
                stroke="var(--color-muted-foreground)"
                strokeDasharray="2 4"
                strokeWidth={1.5}
                dot={false}
                name="CL (mean)"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="claims"
                stroke="var(--color-primary)"
                strokeWidth={2.5}
                dot={{ r: 3.5 }}
                activeDot={{ r: 5 }}
                name="Claims"
              />
              <Line
                type="monotone"
                dataKey="violation"
                stroke="none"
                dot={{ r: 6, fill: "var(--color-destructive)", stroke: "var(--color-destructive)" }}
                name="Out-of-control"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{component.spec}</p>
        <ChartDataTable
          chartTitle={`Claims SPC Control Chart — ${component.label}`}
          columns={[
            { key: "month", label: "Month" },
            { key: "claims", label: "Claims", unit: "count", numeric: true },
            { key: "cl", label: "Center line (mean)", numeric: true },
            { key: "ucl", label: "UCL (+3σ)", numeric: true },
            { key: "lcl", label: "LCL (−3σ)", numeric: true },
            { key: "violation", label: "Out-of-control", numeric: true },
          ]}
          rows={data.map((point) => ({
            month: point.month,
            claims: point.claims,
            cl: point.cl.toFixed(1),
            ucl: point.ucl.toFixed(1),
            lcl: point.lcl.toFixed(1),
            violation: point.violation === null ? null : point.violation,
          }))}
          summary={`${data.length} monthly points for ${component.label}; out-of-control rows show the claims value that breached a ±3σ limit.`}
          contextNote="Synthetic fixture data for human review. Control limits derive from the individuals chart (MR̄/1.128). Production SPC data and quality state will come through the coordinator-owned API contract."
        />
      </div>

      <div className="flex flex-col gap-3">
        <ForecastStat
          label="Center Line (CL)"
          value={cl.toFixed(1)}
          hint={`12-month mean — σ̂ ${sigma.toFixed(2)} (MR̄ / 1.128)`}
          tone="accent"
        />
        <ForecastStat
          label="UCL / LCL (±3σ)"
          value={`${ucl.toFixed(1)} / ${lcl.toFixed(1)}`}
          hint="Shewhart control limits"
          tone="primary"
        />
        <ForecastStat
          label={inControl ? "Process In Control" : "Special-Cause Detected"}
          value={`${lastValue}`}
          hint={
            violations > 0
              ? `${violations} point${violations === 1 ? "" : "s"} outside limits — open 8D review`
              : "Latest point within ±3σ band"
          }
          tone={inControl && violations === 0 ? "success" : "warning"}
        />
        <div className="rounded-xl border border-border/60 bg-background/40 p-4 text-[11px] text-muted-foreground">
          <p className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
            <ShieldCheck className="h-3 w-3 text-primary" /> SPC method
          </p>
          Limits derived from individuals (I-MR) chart with constant d₂ = 1.128. Trigger Nelson Rule
          1 (any point beyond ±3σ) routes to PFMEA owner; sustained shifts (Rule 2 — 9 points one
          side of CL) escalate to 8D-D4.
        </div>
      </div>
    </section>
  );
}

function ReadAcrossSection() {
  const [openSourceId, setOpenSourceId] = useState<string | null>(null);
  const [checklistState, setChecklistState] = useState<Record<string, boolean[]>>({});

  const totals = useMemo(() => {
    let applied = 0,
      review = 0,
      open = 0,
      na = 0,
      targets = 0;
    for (const r of readAcross) {
      for (const t of r.targets) {
        targets++;
        if (t.status === "Applied") applied++;
        else if (t.status === "In Review") review++;
        else if (t.status === "Open") open++;
        else na++;
      }
    }
    const coverage = targets ? Math.round((applied / targets) * 100) : 0;
    return { applied, review, open, na, targets, coverage };
  }, []);

  const openRow = openSourceId
    ? (readAcross.find((r) => r.sourceCaseId === openSourceId) ?? null)
    : null;

  const toggleCheck = (key: string, idx: number) => {
    setChecklistState((prev) => {
      const current = prev[key] ?? Array(READ_ACROSS_CHECKLIST.length).fill(false);
      const next = [...current];
      next[idx] = !next[idx];
      return { ...prev, [key]: next };
    });
  };

  return (
    <section
      className="overflow-hidden rounded-xl border border-border"
      style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Share2 className="h-4 w-4 text-accent" /> Read-Across — propagate 8D lessons
          </h2>
          <p className="text-xs text-muted-foreground">
            Click any row to drill into the source 8D, lesson, and target action checklist
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-success">
            {totals.applied} Applied
          </span>
          <span className="rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-warning">
            {totals.review} In Review
          </span>
          <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-destructive">
            {totals.open} Open
          </span>
          <span className="rounded-full border border-border bg-background/60 px-2 py-0.5 text-muted-foreground">
            Coverage {totals.coverage}%
          </span>
        </div>
      </div>

      <div className="divide-y divide-border/60">
        {readAcross.map((row) => {
          const applied = row.targets.filter((t) => t.status === "Applied").length;
          const actionable = row.targets.filter((t) => t.status !== "Not Applicable").length;
          const pct = actionable ? Math.round((applied / actionable) * 100) : 0;
          return (
            <div
              key={row.sourceCaseId}
              className="grid w-full gap-4 px-5 py-4 text-left transition-colors hover:bg-background/40 lg:grid-cols-[280px_1fr]"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary">
                    {row.sourceCaseId}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Source 8D
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenSourceId(row.sourceCaseId)}
                    aria-label={`Open Read-Across drilldown for ${row.sourceCaseId}`}
                    className="ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent transition-colors hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Drill in →
                  </button>
                </div>
                <p className="text-sm leading-snug">{row.lesson}</p>
                <div className="inline-flex items-center gap-1.5 rounded-md bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
                  <CircleDot className="h-3 w-3 text-accent" /> {row.rootCauseTag}
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
                    <span>Propagation</span>
                    <span>
                      {applied}/{actionable}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-success transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </div>

              <div
                tabIndex={0}
                aria-label={`Read-Across targets for ${row.sourceCaseId}. Use the arrow keys or Shift plus mouse wheel to view additional columns.`}
                className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
              >
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-1.5 pr-3 text-left font-medium">
                        Target product / program
                      </th>
                      <th className="py-1.5 pr-3 text-left font-medium">Owner</th>
                      <th className="py-1.5 pr-3 text-left font-medium">Status</th>
                      <th className="py-1.5 pr-3 text-left font-medium">Action due</th>
                      <th className="py-1.5 text-right font-medium">Potential avoided exposure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.targets.map((t) => (
                      <tr key={t.product + t.program} className="border-t border-border/50">
                        <td className="py-2 pr-3">
                          <div className="font-medium">{t.product}</div>
                          <div className="text-[11px] text-muted-foreground">{t.program}</div>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{t.owner}</td>
                        <td className="py-2 pr-3">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                              readAcrossStatusTone[t.status],
                            )}
                          >
                            {t.status === "Applied" && <Check className="h-3 w-3" />}
                            {t.status === "In Review" && <Activity className="h-3 w-3" />}
                            {t.status === "Open" && <AlertTriangle className="h-3 w-3" />}
                            {t.status === "Not Applicable" && <X className="h-3 w-3" />}
                            {t.status}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground">
                          {format(new Date(t.actionDue + "T00:00:00"), "MMM d, yyyy")}
                        </td>
                        <td className="py-2 text-right font-medium text-success">
                          {t.riskAvoided}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border/60 bg-background/30 px-5 py-3 text-[11px] text-muted-foreground">
        Read-across closes the loop across the portfolio: every D7 (Prevent) lesson is mapped to
        sibling products that share the same component, process, or supplier — turning one fix into
        fleet-wide prevention.
      </div>

      {openRow && (
        <ReadAcrossDrilldown
          row={openRow}
          checklistState={checklistState}
          onToggle={toggleCheck}
          onClose={() => setOpenSourceId(null)}
        />
      )}
    </section>
  );
}

function ReadAcrossDrilldown({
  row,
  checklistState,
  onToggle,
  onClose,
}: {
  row: (typeof readAcross)[number];
  checklistState: Record<string, boolean[]>;
  onToggle: (key: string, idx: number) => void;
  onClose: () => void;
}) {
  const sourceCase = initialCases.find((c) => c.id === row.sourceCaseId);
  const sourcePfmea = pfmeaControl.find((p) => p.caseId === row.sourceCaseId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-border shadow-2xl"
        style={{ background: "var(--gradient-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Share2 className="h-3.5 w-3.5 text-accent" />
              <span className="uppercase tracking-wide">Read-Across drilldown</span>
              <span>·</span>
              <span className="font-mono text-primary">{row.sourceCaseId}</span>
              {sourceCase && (
                <>
                  <span>·</span>
                  <span>{sourceCase.product}</span>
                  <span
                    className={`ml-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${severityTone[sourceCase.severity]}`}
                  >
                    {sourceCase.severity}
                  </span>
                </>
              )}
            </div>
            <h3 className="mt-1 text-lg font-semibold">{sourceCase?.issue ?? row.lesson}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {sourceCase
                ? `Owner ${sourceCase.owner} · ${sourceCase.stage} · ${sourceCase.age}d open`
                : row.rootCauseTag}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-border bg-background/60 p-1.5 hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-110px)] space-y-5 overflow-y-auto p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-background/40 p-3">
              <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                <Layers className="h-3 w-3 text-accent" /> Source 8D snapshot
              </div>
              {sourceCase ? (
                <ul className="space-y-1.5 text-xs">
                  <li>
                    <span className="text-muted-foreground">Product · </span>
                    {sourceCase.product}
                  </li>
                  <li>
                    <span className="text-muted-foreground">Issue · </span>
                    {sourceCase.issue}
                  </li>
                  <li>
                    <span className="text-muted-foreground">Stage · </span>
                    {sourceCase.stage}
                  </li>
                  <li>
                    <span className="text-muted-foreground">Owner · </span>
                    {sourceCase.owner}
                  </li>
                  <li>
                    <span className="text-muted-foreground">Status · </span>
                    {sourceCase.status}
                  </li>
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">Source case archive not loaded.</p>
              )}
            </div>
            <div className="rounded-lg border border-border bg-background/40 p-3">
              <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                <CircleDot className="h-3 w-3 text-accent" /> Root cause & corrective
              </div>
              {sourcePfmea ? (
                <ul className="space-y-1.5 text-xs">
                  <li>
                    <span className="rounded bg-primary/10 px-1 py-0.5 font-mono text-[9px] font-semibold text-primary">
                      D4
                    </span>{" "}
                    <span className="text-muted-foreground">Cause · </span>
                    {sourcePfmea.cause}
                  </li>
                  <li>
                    <span className="rounded bg-accent/10 px-1 py-0.5 font-mono text-[9px] font-semibold text-accent">
                      D5
                    </span>{" "}
                    <span className="text-muted-foreground">Corrective · </span>
                    {sourcePfmea.corrective}
                  </li>
                  <li>
                    <span className="text-muted-foreground">Control · </span>
                    {sourcePfmea.control}
                  </li>
                  <li>
                    <span className="text-muted-foreground">RPN · </span>
                    <span className="font-mono text-destructive line-through">
                      {sourcePfmea.sevBefore * sourcePfmea.occBefore * sourcePfmea.detBefore}
                    </span>
                    <span className="mx-1">→</span>
                    <span className="font-mono font-semibold text-success">
                      {sourcePfmea.sevAfter * sourcePfmea.occAfter * sourcePfmea.detAfter}
                    </span>
                  </li>
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No PFMEA linkage on file.</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-accent/30 bg-accent/5 p-4">
            <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-accent">
              <Sparkles className="h-3 w-3" /> Lesson learned
            </div>
            <p className="text-sm leading-relaxed">{row.lesson}</p>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
              <CircleDot className="h-3 w-3 text-accent" /> {row.rootCauseTag}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              <ClipboardCheck className="h-3 w-3 text-primary" /> Target action checklist — per
              receiving program
            </div>
            <div className="space-y-3">
              {row.targets.map((t) => {
                const key = `${row.sourceCaseId}::${t.product}::${t.program}`;
                const checks =
                  checklistState[key] ?? Array(READ_ACROSS_CHECKLIST.length).fill(false);
                const done = checks.filter(Boolean).length;
                return (
                  <div key={key} className="rounded-lg border border-border bg-background/40 p-3">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium">
                          {t.product}{" "}
                          <span className="text-xs text-muted-foreground">· {t.program}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          Owner {t.owner} · Due{" "}
                          {format(new Date(t.actionDue + "T00:00:00"), "MMM d, yyyy")} · Avoids{" "}
                          {t.riskAvoided}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                            readAcrossStatusTone[t.status],
                          )}
                        >
                          {t.status}
                        </span>
                        <span className="rounded-full bg-background/60 px-2 py-0.5 text-[10px] font-mono text-muted-foreground">
                          {done}/{READ_ACROSS_CHECKLIST.length}
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-1.5">
                      {READ_ACROSS_CHECKLIST.map((item, idx) => {
                        const checked = checks[idx];
                        return (
                          <li key={idx}>
                            <button
                              type="button"
                              onClick={() => onToggle(key, idx)}
                              className="flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left text-xs hover:bg-background/60"
                            >
                              <span
                                className={cn(
                                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                  checked
                                    ? "border-success bg-success text-success-foreground"
                                    : "border-border bg-background",
                                )}
                              >
                                {checked && <Check className="h-3 w-3" />}
                              </span>
                              <span className={cn(checked && "text-muted-foreground line-through")}>
                                {item}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const STANDARD_TONE: Record<"IATF 16949" | "ISO 9001", string> = {
  "IATF 16949": "bg-primary/10 text-primary border-primary/30",
  "ISO 9001": "bg-accent/10 text-accent border-accent/30",
};

function AuditTrailSection() {
  const [filter, setFilter] = useState<"All" | "IATF 16949" | "ISO 9001">("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return auditTrail.filter((e) => {
      if (filter !== "All" && !e.standards.some((s) => s.name === filter)) return false;
      if (!q) return true;
      return (
        e.action.toLowerCase().includes(q) ||
        e.detail.toLowerCase().includes(q) ||
        e.actor.toLowerCase().includes(q) ||
        (e.caseId ?? "").toLowerCase().includes(q) ||
        e.evidence.toLowerCase().includes(q) ||
        e.standards.some(
          (s) => s.clause.toLowerCase().includes(q) || s.title.toLowerCase().includes(q),
        )
      );
    });
  }, [filter, query]);

  const exportAuditCsv = () => {
    const headers = [
      "ID",
      "Timestamp (UTC)",
      "Actor",
      "Action",
      "Detail",
      "Case",
      "Evidence",
      "Standards / Clauses",
    ];
    const rows = filtered.map((e) => [
      e.id,
      e.timestamp,
      e.actor,
      e.action,
      e.detail,
      e.caseId ?? "",
      e.evidence,
      e.standards.map((s) => `${s.name} ${s.clause} (${s.title})`).join(" | "),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Audit trail exported as CSV");
  };

  return (
    <section
      className="overflow-hidden rounded-xl border border-border"
      style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Audit Trail — IATF 16949 & ISO 9001
          </h2>
          <p className="text-xs text-muted-foreground">
            Tamper-evident log of quality actions mapped to clause-level evidence for surveillance &
            customer audits
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clause, action, case…"
              className="h-8 w-48 rounded-md border border-border bg-background/60 pl-7 pr-2 text-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-border text-[11px]">
            {(["All", "IATF 16949", "ISO 9001"] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={cn(
                  "px-2.5 py-1 transition-colors",
                  filter === opt
                    ? "bg-primary text-primary-foreground"
                    : "bg-background/60 hover:bg-muted",
                )}
              >
                {opt}
              </button>
            ))}
          </div>
          <button
            onClick={exportAuditCsv}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <div
        tabIndex={0}
        aria-label="Audit trail entries. Use the arrow keys or Page Down to review additional entries."
        className="max-h-[520px] overflow-y-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <ol className="relative">
          {filtered.map((e, idx) => {
            const ts = new Date(e.timestamp);
            return (
              <li
                key={e.id}
                className={cn(
                  "grid gap-3 px-5 py-3 text-sm transition-colors hover:bg-background/40 lg:grid-cols-[160px_1fr_260px]",
                  idx > 0 && "border-t border-border/60",
                )}
              >
                <div className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
                  <span className="font-mono text-xs text-foreground">
                    {format(ts, "MMM d, yyyy")}
                  </span>
                  <span className="font-mono">{e.timestamp.slice(11, 16)} UTC</span>
                  <span className="font-mono text-[10px] text-muted-foreground/80">{e.id}</span>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{e.action}</span>
                    {e.caseId && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary">
                        {e.caseId}
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">by {e.actor}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{e.detail}</p>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-background/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                    <FileText className="h-3 w-3" /> {e.evidence}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  {e.standards.map((s) => (
                    <div
                      key={s.name + s.clause}
                      className={cn(
                        "rounded-md border px-2 py-1 text-[11px]",
                        STANDARD_TONE[s.name],
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{s.name}</span>
                        <span className="font-mono text-[10px]">cl. {s.clause}</span>
                      </div>
                      <div className="text-[10px] opacity-80">{s.title}</div>
                    </div>
                  ))}
                </div>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="px-5 py-8 text-center text-xs text-muted-foreground">
              No entries match the current filter.
            </li>
          )}
        </ol>
      </div>

      <div className="border-t border-border/60 bg-background/30 px-5 py-3 text-[11px] text-muted-foreground">
        Each entry carries an immutable timestamp, actor, evidence reference and clause mapping —
        directly satisfies IATF 16949 §7.5.3 & ISO 9001 §7.5.3 records control.
      </div>
    </section>
  );
}
